import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
// @mui
import {
  Box,
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetAccountsQuery } from 'src/services/organisation/organisation.service';
import { generateListCreateError, generateListUpdateError } from 'src/services/lists/lists.utils';

// services
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// types
import { ListEntityType } from '../../../services/lists/lists.types';
// components
import FormProvider, { RHFSwitch, RHFTextField, RHFAutocomplete } from '../../../components/hook-form';
import { useGetListByIdQuery, useCreateListMutation, useUpdateListMutation } from '../../../services/lists/lists.service';

// ----------------------------------------------------------------------

interface FormValuesProps {
  name: string;
  description: string;
  entity_type: ListEntityType;
  access_list?: SelectableAccount[];
  entities?: string[];
  register_via_job_board?: boolean;
}

interface Props {
  open: boolean;
  onClose: VoidFunction;
  listId?: string | null;
  entityType: ListEntityType;
  onSuccess?: VoidFunction;
}

interface SelectableAccount {
  id: string;
  first_name: string;
  last_name: string;
}

export default function ListEditorModal({ open, onClose, listId, entityType, onSuccess }: Props) {

  const { t } = useTranslation();

  const authContext = useAuthContext();

  const { canAccess } = useFeatures();

  const [userQuery, setUserQuery] = useState<string>('');

  const { data: accounts } = useGetAccountsQuery({
    page: {
      page: 1,
      per_page: 5,
    },
    search_query: userQuery,
    org_id: authContext.organization.id,

  }, {
    skip: !entityType,
  });

  const { data: currentList } = useGetListByIdQuery(listId as string,
    { skip: !listId }
  );

  const isEdit = !!currentList;

  const [createList, { isLoading: isCreating }] = useCreateListMutation();
  const [updateList, { isLoading: isUpdating }] = useUpdateListMutation();

  const isSubmitting = isCreating || isUpdating;

  const ListSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    entity_type: Yup.string().required(t('validation.required')),
    access_list: Yup.array<{ id: string, first_name: string, last_name: string }>(),
    register_via_job_board: Yup.boolean(),
  });

  const defaultValues = {
    name: currentList?.name || '',
    description: currentList?.description || '',
    entity_type: entityType,
    access_list: currentList?.access_list || [],
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(ListSchema) as any,
    defaultValues,
  });

  const {
    reset,
    handleSubmit
  } = methods;

  useEffect(() => {
    if (open && currentList) {
      reset({
        name: currentList?.name || '',
        description: currentList?.description || '',
        entity_type: entityType,
        access_list: currentList?.access_list || [],
        register_via_job_board: currentList?.register_via_job_board || false,
      });
    }
  }, [open, currentList, reset, entityType]);

  const handleClose = useCallback(() => {
    setUserQuery('');
    onClose();
  }, [onClose]);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      if (isEdit && currentList) {

        await updateList({
          list_id: currentList.id,
          data: {
            name: data.name,
            description: data.description,
            access_list: Array.isArray(data.access_list) ? data.access_list.map((account) => account.id) : [],
            register_via_job_board: data.register_via_job_board
          },
        }).unwrap();

        enqueueSnackbar(t('lists.api.update.success'), {
          variant: 'success'
        });

      } else {

        await createList({
          name: data.name,
          description: data.description,
          entity_type: data.entity_type,
          access_list: Array.isArray(data.access_list) ? data.access_list.map((account) => account.id) : [],
          entities: [],
          register_via_job_board: data.register_via_job_board
        }).unwrap();

        enqueueSnackbar(t('lists.api.create.success'), {
          variant: 'success'
        });
      }

      handleClose();
      if (onSuccess) {
        onSuccess();
      }
      reset();
    } catch (error) {
      if (isEdit) {
        enqueueSnackbar(generateListUpdateError(error), { variant: 'error' });
      }
      else {
        enqueueSnackbar(generateListCreateError(error), { variant: 'error' });
      }
    }
  };

  const selectableAccounts = useMemo(() => {
    if (!accounts?.results) return [];

    let filteredUsers = accounts.results;
    // filter out users already in the access list if mode is edit
    if (isEdit && currentList?.access_list) {
      filteredUsers = filteredUsers.filter((account) => !currentList.access_list.some((user) => user.id === account.id));
    }

    return filteredUsers.map((account) => ({
      id: account.id,
      first_name: account.first_name,
      last_name: account.last_name,
    }));
  }, [accounts, currentList, isEdit]);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setUserQuery(value);
    }, 500),
    [setUserQuery]
  );

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        {isEdit ? t('lists.editor_modal.edit_title') : t('lists.editor_modal.create_title')}
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {
          canAccess(FeatureType.LISTS_ENABLED) ? (
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Stack spacing={3}>
                  <RHFTextField
                    name="name"
                    label={t('lists.editor_modal.name.label')}
                    placeholder={t('lists.editor_modal.name.placeholder')}
                  />

                  <RHFTextField
                    name="description"
                    label={t('lists.editor_modal.description.label')}
                    placeholder={t('lists.editor_modal.description.placeholder')}
                    multiline
                    rows={4}
                  />

                  <RHFAutocomplete
                    name="access_list"
                    multiple
                    label={t('lists.editor_modal.access_list.label')}
                    placeholder={t('lists.editor_modal.access_list.placeholder')}
                    options={selectableAccounts as SelectableAccount[]}
                    renderOption={({ key, ...rest }, option: SelectableAccount) => (
                      <li {...rest} key={option.id}>
                        <Stack direction="row" justifyContent="space-between" width="100%">
                          <Typography variant="body2">
                            {`${option.first_name} ${option.last_name}`}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    getOptionLabel={(option: string | SelectableAccount) =>
                      typeof option === 'string'
                        ? option
                        : `${option.first_name} ${option.last_name}`
                    }
                    onInputChange={(_e, value) => onSearchQueryChange(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />

                  {
                    entityType === ListEntityType.CANDIDATE && (
                      <RHFSwitch
                        name="register_via_job_board"
                        label={
                          <Stack>
                            <Typography variant="body2">
                              {t('lists.editor_modal.register_via_job_board.label')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('lists.editor_modal.register_via_job_board.helper_text')}
                            </Typography>
                          </Stack>
                        }
                      />
                    )
                  }

                </Stack>
              </Box>
            </DialogContent>
          ) :
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <FeatureLockedListView text={t('billing.subscription_required')} />
            </Box>
        }

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            {t('common.cancel')}
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            disabled={!canAccess(FeatureType.LISTS_ENABLED) || isSubmitting}
            loading={isSubmitting}
          >
            {!isEdit ? t('common.create') : t('common.update')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>

    </Dialog>
  );
}
